import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import {
  DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  useDebounce,
} from '@ipfabric/ui-components/hooks/useDebounce';
import {
  ENTER,
  ESCAPE,
} from '@ipfabric/ui-components/utils/keyboard/constants';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { fitEdgeToView } from 'components/Graph/commands/edges/fitEdgeToView';
import { fitNodeToView } from 'components/Graph/commands/nodes/fitNodeToView';
import { TextInput } from 'components/TextInput/TextInput';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FC, KeyboardEvent } from 'react';
import { trimValueLeft } from 'utils/formValidation/trimValue';
import { useGraphContextSelectors } from '../GraphContext';
import { PositionIcon } from '../GraphSidePanel/icons/PositionIcon';
import { useGraphSearch } from './useGraphSearch';

interface Props {
  onClose: () => void;
  value: string;
  setValue: (value: string) => void;
}

export const GraphToolbarSearchForm: FC<Props> = (props) => {
  const {
    canvasGraph: {
      graphToolbar: { search: msgSearch },
    },
  } = useLocale();
  const debounce = useDebounce(DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [dropdownHovered, setDropdownHovered] = useState<boolean>(false);
  const [inputFocused, setInputFocused] = useState<boolean>(true);
  const { searchMatches, ...graphSearch } = useGraphSearch();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
  });

  const hasNodeMatches =
    (searchMatches.nodeExactMatch && searchMatches.nodes.length > 1) ||
    (!searchMatches.nodeExactMatch && searchMatches.nodes.length > 0);
  const dropdownVisible =
    (dropdownHovered || inputFocused) &&
    (hasNodeMatches || searchMatches.edges.length > 0);

  const reset = () => {
    props.setValue('');
    graphSearch.search('');
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;

    props.setValue(query);
    debounce(() => {
      graphSearch.matchQuery(query);
    });
  };

  const submit = () => {
    const trimmedValue = props.value.trim();
    graphSearch.search(trimmedValue);
    searchInputRef.current?.blur();
    setDropdownHovered(false);

    props.setValue(trimmedValue);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === ESCAPE) {
      reset();
      props.onClose();
    }

    if (e.keyCode === ENTER) {
      submit();
    }
  };

  const handleMouseEnter = () => {
    setDropdownHovered(true);
  };

  const handleMouseLeave = () => {
    setDropdownHovered(false);
  };

  const handleAutocompleteItemClick = (query: string) => {
    props.setValue(query);
    graphSearch.search(query);

    searchInputRef.current?.blur();
    setDropdownHovered(false);
  };

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  return (
    <div
      className='graph-toolbar__form'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TextInput
        onChange={trimValueLeft(handleChange)}
        autoComplete='off'
        className={classNames(
          dropdownVisible && 'graph-toolbar__form-input--with-dropdown',
        )}
        inputRef={searchInputRef}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          setInputFocused(false);
        }}
        onFocus={() => {
          setInputFocused(true);
        }}
        name='search'
        placeholder={msgSearch.placeholder}
        value={props.value}
        type='search'
      />
      {dropdownVisible && (
        <ScrollView
          autoHeight
          autoHeightMax={window.innerHeight - 90}
          className='graph-toolbar__form-dropdown'
        >
          <ul className='graph-toolbar__form-list'>
            {searchMatches.nodes.map(({ label, nodeData }) => (
              <li
                className='graph-toolbar__form-dropdown-item'
                key={nodeData.id}
                onMouseEnter={() => {
                  graphSearch.previewNode(nodeData);
                }}
                onMouseLeave={() => {
                  graphSearch.resetPreview();
                }}
                onClick={() => {
                  handleAutocompleteItemClick(label);
                }}
              >
                <div className='grow overflow-hidden'>{label}</div>
                <Tooltip content={msgSearch.navigate} placement='left'>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      ctxProps.dispatch(fitNodeToView, { nodeData });
                    }}
                    className='graph-toolbar__form-dropdown-button'
                    type='button'
                  >
                    <PositionIcon
                      title={msgSearch.navigate}
                      height={14}
                      width={14}
                    />
                  </button>
                </Tooltip>
              </li>
            ))}
            {searchMatches.edges.map(({ label, sublabel, edgeData }) => (
              <li
                className='graph-toolbar__form-dropdown-item'
                key={edgeData.id}
                onMouseEnter={() => {
                  graphSearch.previewEdge(edgeData);
                }}
                onMouseLeave={() => {
                  graphSearch.resetPreview();
                }}
                onClick={() => {
                  handleAutocompleteItemClick(label);
                }}
              >
                <div className='grow overflow-hidden'>
                  <div>{label}</div>
                  <Tooltip content={sublabel} placement='top'>
                    <div className='graph-toolbar__form-dropdown-item-sublabel'>
                      {sublabel}
                    </div>
                  </Tooltip>
                </div>
                <Tooltip content={msgSearch.navigate} placement='left'>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      ctxProps.dispatch(fitEdgeToView, { edgeData });
                    }}
                    className='graph-toolbar__form-dropdown-button'
                    type='button'
                  >
                    <PositionIcon
                      title={msgSearch.navigate}
                      height={14}
                      width={14}
                    />
                  </button>
                </Tooltip>
              </li>
            ))}
          </ul>
        </ScrollView>
      )}
    </div>
  );
};
